import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Send, Eye, Menu } from 'react-feather';
import ChatMessage from '../components/ChatMessage';
import FeedbackButtons from '../components/FeedbackButtons';
import SatisfactionAndEscalation from '../components/SatisfactionAndEscalation';
import ConversationStarter from '../components/ConversationStarter';
import TeachingForm from '../components/TeachingForm';
import ImpersonationModal from '../components/ImpersonationModal';
import ConversationSidebar from '../components/ConversationSidebar';
import ShareModal from '../components/ShareModal';
import { useAuth } from '../contexts/AuthContext';
import { useConversations } from '../hooks/useConversations';
import '../styles/cookie-debug.css';

interface Source {
  url?: string;
  title?: string;
  score?: number;
  chunk?: {
    source?: {
      url?: string;
      title?: string;
    };
  };
}

interface TeachingResponse {
  requiresApproval?: boolean;
  teachingGenerated?: any;
}

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  metadata?: {
    confidence?: number;
    responseTime?: number;
    sources?: Source[];
    teachingResponse?: TeachingResponse;
    responseId?: string;
    conversationId?: string;
    isError?: boolean;
    showSatisfactionCheck?: boolean;
    showEscalationDialog?: boolean;
  };
}

const ChatPage: React.FC = () => {
  // Authentication
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  // Conversation history management
  const {
    conversations,
    loading: conversationsLoading,
    deleteConversation,
    searchConversations,
    updateConversation
  } = useConversations();

  // Conversation state management
  const [conversationReady, setConversationReady] = useState(false);
  const [userRole, setUserRole] = useState<string>('');
  const [initialMessage, setInitialMessage] = useState<string>('');
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showQuickQuestions, setShowQuickQuestions] = useState(true);
  const [currentConversationId, setCurrentConversationId] = useState<string | undefined>();
  const [showCookieDebug, setShowCookieDebug] = useState(false);
  const [cookieDebugInfo, setCookieDebugInfo] = useState<any>(null);
  const [showTeachingForm, setShowTeachingForm] = useState(false);
  const [showImpersonationModal, setShowImpersonationModal] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [shareConversationId, setShareConversationId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const sessionId = useRef('web_' + Date.now()).current;

  const _scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  void _scrollToBottom; // Suppress unused warning - kept for potential future use

  const _fetchCookieDebugInfo = async () => {
    try {
      const response = await fetch('/debug/cookies', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Filter to show only authentication-relevant cookies
        const authCookies = ['ATLLogin', 'treeline.session', 'ai_authUser', 'ASP.NET_SessionId', 
                            'EdelweissAuth', 'EdelweissSession', '.AspNetCore.Identity.Application'];
        const filteredCookies: any = {};
        
        for (const cookieName of authCookies) {
          if (data.cookies && data.cookies[cookieName]) {
            filteredCookies[cookieName] = data.cookies[cookieName];
          }
        }
        
        setCookieDebugInfo({
          ...data,
          cookies: filteredCookies,
          allCookieNames: Object.keys(data.cookies || {})
        });
        setShowCookieDebug(true);
      }
    } catch (error) {
      console.error('Failed to fetch cookie debug info:', error);
      setCookieDebugInfo({ error: 'Failed to fetch cookie information' });
      setShowCookieDebug(true);
    }
  };
  void _fetchCookieDebugInfo; // Suppress unused warning - kept for cookie debug feature

  const scrollToNewMessage = () => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      // If the last message is from assistant, scroll to its top
      if (lastMessage.type === 'assistant') {
        const messageElement = document.querySelector(`[data-response-id="${lastMessage.metadata?.responseId}"]`);
        if (messageElement) {
          messageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }
    }
    // Fallback to bottom scroll for user messages or if element not found
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const autoResize = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  };

  useEffect(() => {
    scrollToNewMessage();
  }, [messages]);

  useEffect(() => {
    // Focus input on mount
    inputRef.current?.focus();
    
    // Load configuration
    loadConfiguration();
    
    // Check API health
    checkAPIHealth();
    
    // Load widget for testing in development
    loadWidgetForTesting();
  }, []);

  const loadWidgetForTesting = async () => {
    // Simple environment check using NODE_ENV
    const _isClientDevelopment = process.env.NODE_ENV === 'development';
    void _isClientDevelopment; // Suppress unused warning - logged for debugging

    // Verify server environment matches
    let serverEnvironment = 'unknown';
    try {
      // Use proxied health endpoint (Vite proxy forwards to API server)
      const envResponse = await fetch('/health');
      if (envResponse.ok) {
        const envData = await envResponse.json();
        serverEnvironment = envData.environment || 'unknown';
        console.log('✅ Server environment verified:', serverEnvironment);
      }
    } catch (e) {
      console.warn('Could not verify server environment, assuming production for safety');
      serverEnvironment = 'production';
    }

    const isServerDev = serverEnvironment === 'development';
    const shouldLoadWidget = isServerDev;

    console.log('🔐 Widget security check:', {
      serverEnvironment,
      shouldLoadWidget
    });
    
    if (shouldLoadWidget) {
      const widgetApiKey = import.meta.env.VITE_WIDGET_API_KEY;
      if (!widgetApiKey) {
        console.error('❌ VITE_WIDGET_API_KEY not set in .env — cannot load dev widget');
        return;
      }
      console.log('🚀 Loading React widget for local dev testing...');
      
      // Load CSS first
      const cssLink = document.createElement('link');
      cssLink.rel = 'stylesheet';
      cssLink.href = '/widget/eddy-widget.css';
      document.head.appendChild(cssLink);

      // Create script element for React widget
      const script = document.createElement('script');
      script.src = '/widget/eddy-widget.js';
      script.onload = () => {
        // Initialize React widget after script loads
        try {
          if (typeof (window as any).EddyWidget !== 'undefined') {
            // Fetch the actual configuration from the widget API
            console.log('📡 Fetching widget configuration from admin system...');
            
            // Create fetch with longer timeout for blob storage operations
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
            
            fetch('/api/widget/config', {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'X-Widget-Key': widgetApiKey,
                'X-Widget-Domain': window.location.hostname
              },
              signal: controller.signal
            })
            .then(response => {
              clearTimeout(timeoutId); // Clear timeout on successful response
              return response.json();
            })
            .then(configResponse => {
              if (configResponse.success) {
                const widgetConfig = configResponse.data;
                console.log('✅ Fetched widget config:', widgetConfig);
                
                // Use the fetched configuration
                const widget = (window as any).EddyWidget.create({
                  apiKey: widgetApiKey,
                  apiEndpoint: '/api/widget/chat',
                  position: widgetConfig.position || 'bottom-right',
                  title: widgetConfig.title || 'Ask Eddy',
                  subtitle: widgetConfig.subtitle || 'Edelweiss AI Assistant',
                  primaryColor: widgetConfig.primaryColor,
                  accentColor: widgetConfig.accentColor,
                  autoOpen: widgetConfig.autoOpen || false,
                  showSources: widgetConfig.showSources !== false,
                  placeholder: widgetConfig.placeholder || 'Ask me anything about Edelweiss...',
                  quickQuestions: widgetConfig.quickQuestions || []
                });
                
                widget.init();
                console.log('✅ React widget initialized with live admin configuration');
              } else {
                console.error('❌ Failed to fetch widget config:', configResponse.error);
                // Fallback to minimal config
                const widget = (window as any).EddyWidget.create({
                  apiKey: widgetApiKey,
                  apiEndpoint: '/api/widget/chat'
                });
                widget.init();
              }
            })
            .catch(error => {
              clearTimeout(timeoutId); // Clear timeout on error
              
              if (error.name === 'AbortError') {
                console.error('❌ Widget configuration request timed out after 30 seconds (blob storage issues?)');
              } else {
                console.error('❌ Failed to fetch widget configuration:', error);
              }
              
              // Fallback to minimal config with default quick questions
              console.log('🔄 Using fallback configuration...');
              const widget = (window as any).EddyWidget.create({
                apiKey: widgetApiKey,
                apiEndpoint: '/api/widget/chat',
                title: 'Ask Eddy',
                subtitle: 'Edelweiss AI Assistant',
                position: 'bottom-right',
                quickQuestions: [
                  'What is Edelweiss?',
                  'How do I create an order?'
                ]
              });
              widget.init();
              console.log('✅ Widget initialized with fallback configuration');
            });
          } else {
            console.error('❌ EddyWidget not found after script load');
          }
        } catch (error) {
          console.error('❌ Failed to initialize React widget:', error);
        }
      };
      
      script.onerror = () => {
        console.error('❌ Failed to load React widget script');
      };
      
      // Add to document
      document.head.appendChild(script);
    } else {
      console.log('🚫 Widget testing disabled:', {
        reason: !isServerDev ? `Server environment is ${serverEnvironment}` : 'Unknown',
        serverEnvironment
      });
    }
  };

  const loadConfiguration = async () => {
    try {
      const response = await fetch('/api/config');
      if (response.ok) {
        const config = await response.json();
        const introMessage = config.interface?.introductoryMessage?.fullMessage;
        
        if (introMessage) {
          setInitialMessage(introMessage.replace(/\n/g, '<br>'));
        } else {
          setInitialMessage('Hello! I\'m Eddy, your Edelweiss Assistant. I can help you with questions about the Edelweiss platform, including orders, digital review copies, collections, event grids, and more.<br><br>What would you like to know about Edelweiss today?');
        }
      }
    } catch (error) {
      console.log('Using default introductory message due to config load error:', error);
      setInitialMessage('Hello! I\'m Eddy, your Edelweiss Assistant. I can help you with questions about the Edelweiss platform, including orders, digital review copies, collections, event grids, and more.<br><br>What would you like to know about Edelweiss today?');
    }
  };

  const checkAPIHealth = async () => {
    try {
      const response = await fetch('/api/chat/health');
      if (!response.ok) {
        showAPIError();
      }
    } catch (error) {
      showAPIError();
    }
  };

  const showAPIError = () => {
    const banner = document.createElement('div');
    banner.className = 'fixed top-0 left-0 right-0 bg-red-500 text-white p-4 text-center z-50';

    const strong = document.createElement('strong');
    strong.textContent = '\u26A0\uFE0F API Connection Error:';
    const text = document.createTextNode(' The chat server is not running. Please start the server with ');
    const code = document.createElement('code');
    code.className = 'bg-red-700 px-2 py-1 rounded';
    code.textContent = 'npm run dev';

    banner.append(strong, text, code);
    document.body.appendChild(banner);
  };

  // Role collection handlers
  const handleRoleCollected = (role: string) => {
    setUserRole(role);
    console.log(`🏴‍☠️ User role set to: ${role || 'none (skipped)'}`);
  };

  const _handleRoleChanged = (newRole: string) => {
    setUserRole(newRole);
    console.log(`🏴‍☠️ User role changed to: ${newRole || 'none (cleared)'}`);

    // Add a system message to indicate the role change
    const roleChangeMessage: Message = {
      id: Date.now().toString(),
      type: 'assistant',
      content: getRoleChangeMessage(newRole),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, roleChangeMessage]);
  };
  void _handleRoleChanged; // Suppress unused warning - kept for future role change feature

  const getRoleChangeMessage = (newRole: string): string => {
    if (!newRole) {
      return 'Understood! I\'ve cleared your role preference. I\'ll provide general guidance from now on.';
    }
    
    const roleMessages: { [key: string]: string } = {
      'bookseller': 'Perfect! I\'ve updated your profile to bookseller. I\'ll now focus on retail operations, inventory, and customer-facing guidance.',
      'librarian': 'Excellent! I\'ve set your role to librarian. I\'ll prioritize acquisition processes, institutional workflows, and collection development.',
      'publisher': 'Great! I\'ve marked you as a publisher. I\'ll focus on catalog management, metadata, and publishing workflows.',
      'reviewer': 'Perfect! I\'ve updated your role to reviewer. I\'ll emphasize review copies, embargoes, and critic-specific processes.',
      'blogger': 'Wonderful! I\'ve set your role to book blogger. I\'ll focus on promotional materials, review programs, and content creation support.',
      'sales_rep': 'Excellent! I\'ve updated your profile to sales representative. I\'ll prioritize territory management, client relations, and sales workflows.',
      'other': 'Understood! I\'ve set your role to "other" and I\'ll provide general book industry guidance.'
    };

    return roleMessages[newRole] || 'I\'ve updated your role preference and will adjust my responses accordingly.';
  };

  const handleConversationReady = () => {
    setConversationReady(true);
    
    // Add the initial assistant message after role collection
    const welcomeMessage: Message = {
      id: '1',
      type: 'assistant',
      content: getPersonalizedWelcomeMessage(),
      timestamp: new Date()
    };
    
    setMessages([welcomeMessage]);
    
    // Focus input after conversation is ready
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const getPersonalizedWelcomeMessage = (): string => {
    // Get personalized greeting based on authentication status
    const greeting = isAuthenticated && user?.name ? `Hi ${user.name}!` : 'Hello!';
    
    if (!userRole) {
      return initialMessage || `${greeting} I'm Eddy, your Edelweiss Assistant. What would you like to know about Edelweiss today?`;
    }

    const roleMessages: { [key: string]: string } = {
      'bookseller': `${greeting} I'm Eddy, and I'm here to help you navigate Edelweiss as a bookseller. Whether you need help with orders, inventory management, or customer relations, I've got the guidance you need!<br><br>What can I help you with today?`,
      'librarian': `${greeting} I'm Eddy, and I understand the unique challenges of library acquisitions and patron services. I can guide you through collection development, budget planning, and institutional processes.<br><br>How can I assist your library today?`,
      'publisher': `${greeting} I'm Eddy, and I know the complexities of catalog management, metadata, and distribution. Let me help you navigate title setup, sales data, and publisher relations.<br><br>What publishing challenge can I help with?`,
      'reviewer': `${greeting} I'm Eddy, and I understand your need for advance copies, review embargoes, and digital access. I can help you with galleys, ARCs, and the review copy process.<br><br>What reviewing needs can I assist with?`,
      'blogger': `${greeting} I'm Eddy, and I know how important promotional materials and review copies are for your content creation. I can help you navigate the blogger and influencer processes.<br><br>How can I support your blogging work?`,
      'sales_rep': `${greeting} I'm Eddy, and I understand the challenges of territory management, client relations, and commission tracking. I can help you with account management and order processes.<br><br>What sales challenges can I help with?`,
      'other': `${greeting} I'm Eddy, your Edelweiss Assistant. I can help you with any questions about the Edelweiss platform, from orders and digital review copies to collections and event grids.<br><br>What can I help you with today?`
    };

    return roleMessages[userRole] || roleMessages['other'];
  };

  const askQuickQuestion = (question: string) => {
    setInput(question);
    handleSubmit(new Event('submit') as any);
  };

  const handleTeachingSubmit = (_teachingData: any) => {
    // Close teaching form after submission (teachingData available if needed)
    setShowTeachingForm(false);
    
    // You could optionally add a success message to the chat here
    const successMessage: Message = {
      id: Date.now().toString(),
      type: 'assistant',
      content: 'Teaching successfully created.',
      timestamp: new Date(),
      metadata: {
        confidence: 1.0,
        responseTime: 0,
        sources: [],
        showSatisfactionCheck: false
      }
    };
    setMessages(prev => [...prev, successMessage]);
    
    // Scroll to bottom to show the success message
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
  };

  const handleTeachingCancel = () => {
    setShowTeachingForm(false);
  };

  const handleSelectConversation = async (conversationId: string) => {
    try {
      const response = await fetch(`/api/conversations/${conversationId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const loadedMessages: Message[] = data.data.messages.map((msg: any) => ({
          id: msg.id,
          type: msg.role,
          content: msg.content,
          timestamp: new Date(msg.timestamp),
          metadata: {
            confidence: msg.confidence,
            sources: msg.sources
          }
        }));
        setMessages(loadedMessages);
        setCurrentConversationId(conversationId);
        setConversationReady(true);
        setShowQuickQuestions(false);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  const handleNewConversation = () => {
    setMessages([]);
    setCurrentConversationId(undefined);
    setConversationReady(false);
    setShowQuickQuestions(true);
  };

  const handleShareConversation = (conversationId: string) => {
    setShareConversationId(conversationId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const messageContent = input.trim();
    
    // Check if this is a teach/train command for authenticated users with teaching capabilities
    const teachCommands = /^(teach|train)(\s|$)/i;
    if (teachCommands.test(messageContent) && isAuthenticated && user && user.capabilities?.canUseTeaching) {
      // Clear input and show teaching form instead of processing as chat
      setInput('');
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
      setShowQuickQuestions(false);
      setShowTeachingForm(true);
      return; // Exit early, don't send to chat API
    }
    
    // Clear input and hide quick questions
    setInput('');
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    setShowQuickQuestions(false);

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: messageContent,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      console.log(`Sending message: "${messageContent}" with sessionId: ${sessionId} and role: ${userRole}`);
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: messageContent,
          sessionId: sessionId,
          conversationId: currentConversationId,
          userInfo: {
            role: userRole || undefined
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      
      // Check if response is successful
      if (!data.success) {
        throw new Error(data.error || 'API request failed');
      }
      
      // Check if this is a teaching response
      if (data.data.teachingResponse?.requiresApproval && data.data.teachingResponse?.teachingGenerated) {
        (window as any).pendingTeachingGeneration = data.data.teachingResponse.teachingGenerated;
      }

      // Update currentConversationId if returned by the server
      if (data.data?.conversationId) {
        setCurrentConversationId(data.data.conversationId);
        // Update conversation list with new title (real-time update)
        if (data.data?.conversationTitle) {
          updateConversation(data.data.conversationId, data.data.conversationTitle);
        }
      }
      
      // Classify response to determine if satisfaction check should be shown
      let showSatisfactionCheck = false;
      try {
        const classificationResponse = await fetch('/api/chat/classify-response', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            response: data.data.answer,
            hasRoleClarification: false,
            confidence: data.data.confidence || 1.0
          }),
        });

        if (classificationResponse.ok) {
          const classificationData = await classificationResponse.json();
          showSatisfactionCheck = classificationData.data.shouldShowSatisfactionCheck;
          console.log(`Response classification: ${classificationData.data.classification.classificationType}, show satisfaction: ${showSatisfactionCheck}`);
        }
      } catch (error) {
        console.error('Failed to classify response, defaulting to showing satisfaction check:', error);
        showSatisfactionCheck = true; // Default to showing satisfaction check if classification fails
      }

      // IMPORTANT: If showEscalationDialog is true, we MUST render SatisfactionAndEscalation
      // even though showSatisfactionCheck might be false. The component will skip the satisfaction
      // step and go directly to the escalation dialog.
      if (data.data.showEscalationDialog) {
        showSatisfactionCheck = true;
        console.log('Low-confidence response detected - showing escalation dialog immediately');
      }
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: data.data.answer,
        timestamp: new Date(),
        metadata: {
          confidence: data.data.confidence,
          responseTime: data.data.responseTime,
          sources: data.data.sources,
          teachingResponse: data.data.teachingResponse,
          responseId: data.data.responseId,
          conversationId: data.data.conversationId,
          showSatisfactionCheck: showSatisfactionCheck,
          showEscalationDialog: data.data.showEscalationDialog
        }
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error: any) {
      console.error('Error:', error);
      let errorMessage = 'Sorry, I encountered an error. Please try again.';
      
      // Provide more specific error messages
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        errorMessage = 'Unable to connect to the API server. Please ensure the server is running on port 3001.';
      } else if (error.message.includes('Network response was not ok')) {
        errorMessage = `Server error: ${error.message}. The API server may not be configured correctly.`;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      const errorMsg: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: errorMessage,
        timestamp: new Date(),
        metadata: { isError: true }
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const getOriginalQuestion = (assistantMessage: Message): string => {
    // Find the index of the current assistant message
    const assistantIndex = messages.findIndex(msg => msg.id === assistantMessage.id);
    
    // Look backwards from the assistant message to find the preceding user message
    for (let i = assistantIndex - 1; i >= 0; i--) {
      if (messages[i].type === 'user') {
        return messages[i].content;
      }
    }
    
    // Fallback: return empty string if no user message found
    return '';
  };

  // Determine which interactive components to show for a message
  const getMessageActions = (message: Message) => {
    const { metadata } = message;
    return {
      showFeedback: message.type === 'assistant' && metadata?.responseId && !metadata?.isError && !metadata?.showSatisfactionCheck,
      showSatisfactionCheck: message.type === 'assistant' && metadata?.responseId && !metadata?.isError && metadata?.showSatisfactionCheck
    };
  };

  // Make expandSources available globally for onclick handlers (used by ChatMessage)
  useEffect(() => {
    (window as any).expandSources = (messageId: string) => {
      const additionalSources = document.getElementById(messageId + '_additional');
      const expandLink = document.querySelector(`[onclick="window.expandSources && window.expandSources('${messageId}')"]`);

      if (additionalSources && expandLink) {
        additionalSources.style.display = 'block';
        expandLink.parentElement!.style.display = 'none';
      }
    };
  }, []);

  // Replace feather icons after every render
  useEffect(() => {
    try {
      if (typeof (window as any).feather !== 'undefined') {
        (window as any).feather.replace();
      }
    } catch (error) {
      console.error('Error replacing feather icons:', error);
    }
  });

  // Hold UI until auth status is resolved to avoid flicker
  if (authLoading) {
    return (
      <div className="chat-container">
        <div className="auth-loading" style={{ minHeight: '80vh' }}>
          <div className="spinner-large"></div>
          <p>Preparing your chat…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      {/* Header - Static across full width */}
      <header className="bg-eddy-blue text-white p-3 flex items-center flex-shrink-0">
        {/* Mobile menu button */}
        {isAuthenticated && (
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="lg:hidden mr-3 p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Open conversation history"
          >
            <Menu size={20} />
          </button>
        )}

        <img className="chat-logo" src="/images/eddy-logo.png" alt="Eddy" />

        <div className="ml-auto flex items-center gap-3">
          {/* Impersonation button for Owner users and ZTR users (for audience filtering) */}
          {(user?.impersonation?.canImpersonate || user?.impersonation?.canImpersonateAudience) && (
            <button
              onClick={() => setShowImpersonationModal(true)}
              className={`flex items-center space-x-1 px-3 py-2 rounded-lg border transition-colors text-sm font-medium ${
                user.impersonation.isImpersonating
                  ? 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200'
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
              title={user?.impersonation?.canImpersonate ? "Role Impersonation" : "Audience Filter Testing"}
            >
              <Eye size={14} />
              <span>
                {user.impersonation.isImpersonating
                  ? user.impersonation.effectiveAudience
                    ? `As ${user.impersonation.effectiveAudience}`
                    : `As ${user.impersonation.effectiveRole === 'admin' ? 'Admin' : 'User'}`
                  : 'Impersonate'
                }
              </span>
              {user.impersonation.isImpersonating && (
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></div>
              )}
            </button>
          )}

          {/* Show Admin button only for ZTR users */}
          {isAuthenticated && user?.isZTRUser && (
            <Link
              to="/admin"
              className="settings-toggle"
              style={{ textDecoration: 'none', color: 'inherit' }}
              title={`Admin Dashboard (${user.orgId})`}
            >
              <Shield size={18} />
              <span>Admin</span>
            </Link>
          )}
        </div>
      </header>

      {/* Main content area - sidebar + chat */}
      <div className="flex flex-1 min-h-0 bg-gray-100">
        {/* Conversation Sidebar */}
        <ConversationSidebar
          onSelectConversation={handleSelectConversation}
          onNewConversation={handleNewConversation}
          selectedConversationId={currentConversationId}
          isAuthenticated={isAuthenticated}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          conversations={conversations}
          loading={conversationsLoading}
          onDeleteConversation={deleteConversation}
          onSearchConversations={searchConversations}
          onShareConversation={handleShareConversation}
        />

        {/* Chat Area */}
        <main className="flex-1 flex flex-col min-w-0">
        <div className="chat-container">
        {/* Cookie Debug Modal */}
      {showCookieDebug && (
        <div className="modal-overlay" onClick={() => setShowCookieDebug(false)}>
          <div className="modal-content cookie-debug-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🍪 Cookie Authentication Debug</h3>
              <button className="close-button" onClick={() => setShowCookieDebug(false)}>×</button>
            </div>
            <div className="modal-body">
              {cookieDebugInfo?.error ? (
                <div className="error-message">{cookieDebugInfo.error}</div>
              ) : (
                <>
                  <div className="debug-section">
                    <h4>Authentication Cookies Found:</h4>
                    {Object.keys(cookieDebugInfo?.cookies || {}).length > 0 ? (
                      <div className="cookie-list">
                        {Object.entries(cookieDebugInfo.cookies).map(([name, info]: [string, any]) => (
                          <div key={name} className="cookie-item">
                            <div className="cookie-name">{name}</div>
                            <div className="cookie-value">
                              {info.fullValue?.includes('EmailAddress=') ? (
                                <div>
                                  <div>Email: {new URLSearchParams(info.fullValue).get('EmailAddress')}</div>
                                  <div>Session: {new URLSearchParams(info.fullValue).get('sess')?.substring(0, 20)}...</div>
                                </div>
                              ) : (
                                <div>{info.fullValue?.substring(0, 100)}{info.fullValue?.length > 100 ? '...' : ''}</div>
                              )}
                            </div>
                            <div className="cookie-meta">Length: {info.length} chars</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-cookies">No authentication cookies found</div>
                    )}
                  </div>
                  
                  <div className="debug-section">
                    <h4>All Cookies Present:</h4>
                    <div className="all-cookies">
                      {cookieDebugInfo?.allCookieNames?.join(', ') || 'None'}
                    </div>
                  </div>
                  
                  <div className="debug-section">
                    <h4>Connection Info:</h4>
                    <div>Origin: {cookieDebugInfo?.origin || window.location.origin}</div>
                    <div>User Agent: {cookieDebugInfo?.userAgent}</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Messages */}
      <div className="chat-messages" id="chatMessages">
        <div className="chat-messages-inner">
        {!conversationReady ? (
          /* Show role collection flow before starting conversation */
          <ConversationStarter
            onRoleCollected={handleRoleCollected}
            onConversationReady={handleConversationReady}
            initialMessage={initialMessage}
            showRoleCollection={true}
          />
        ) : (
          /* Show normal chat messages after role collection */
          <>
            {messages.map((message) => {
              const { showFeedback, showSatisfactionCheck } = getMessageActions(message);
              return (
                <ChatMessage
                  key={message.id}
                  id={message.id}
                  role={message.type}
                  content={message.content}
                  confidence={message.metadata?.confidence}
                  responseTime={message.metadata?.responseTime}
                  sources={message.metadata?.sources}
                  responseId={message.metadata?.responseId}
                  conversationId={message.metadata?.conversationId}
                >
                  {showSatisfactionCheck && (
                    <SatisfactionAndEscalation
                      responseId={message.metadata?.responseId || ''}
                      conversationId={message.metadata?.conversationId || ''}
                      messageId={message.id}
                      originalQuestion={getOriginalQuestion(message)}
                      originalAnswer={message.content}
                      originalResponse={message.content}
                      conversationHistory={messages}
                      isAuthenticated={isAuthenticated}
                      canUseTeaching={user?.capabilities?.canUseTeaching === true}
                      authenticatedUser={user}
                      showEscalationDialog={message.metadata?.showEscalationDialog}
                    />
                  )}
                  {showFeedback && (
                    <FeedbackButtons
                      responseId={message.metadata?.responseId || ''}
                      conversationId={message.metadata?.conversationId || ''}
                      messageId={message.id}
                      originalQuestion={getOriginalQuestion(message)}
                      originalAnswer={message.content}
                      originalSources={message.metadata?.sources || []}
                      conversationHistory={messages}
                    />
                  )}
                </ChatMessage>
              );
            })}

            <div ref={messagesEndRef} />
          </>
        )}
        </div>
      </div>

      {/* Thinking indicator */}
      <div className={`thinking-indicator ${isLoading ? 'show' : ''}`} id="thinkingIndicator">
        <span>🤔 Eddy is thinking</span>
        <div className="thinking-dots">
          <div className="thinking-dot"></div>
          <div className="thinking-dot"></div>
          <div className="thinking-dot"></div>
        </div>
      </div>

      {/* Input - only show when conversation is ready */}
      {conversationReady && (
        <div className="chat-input-container">
          <div className="chat-input-inner">
            {showQuickQuestions && (
              <div className="quick-questions" id="quickQuestions">
                <button className="quick-question" onClick={() => askQuickQuestion('What is Edelweiss?')}>What is Edelweiss?</button>
                <button className="quick-question" onClick={() => askQuickQuestion('How do I create an order?')}>How to create orders?</button>
                <button className="quick-question" onClick={() => askQuickQuestion('How do I request review copies?')}>Request review copies</button>
                <button className="quick-question" onClick={() => askQuickQuestion('What are event grids?')}>Event grids</button>
                <button className="quick-question" onClick={() => askQuickQuestion('How do I manage collections?')}>Manage collections</button>
              </div>
            )}

            <form className="chat-input-form" onSubmit={handleSubmit}>
              <textarea
                ref={inputRef}
                id="messageInput"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  autoResize(e.target);
                }}
                placeholder="Ask me anything about Edelweiss..."
                className="chat-input"
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="send-button"
                id="sendButton"
              >
                <Send size={20} style={{ transform: 'rotate(45deg) translate(-3px, 2px)' }} />
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Teaching Form Modal */}
      {showTeachingForm && isAuthenticated && user && user.capabilities?.canUseTeaching && (
        <TeachingForm
          originalQuestion=""
          originalAnswer=""
          conversationHistory={messages}
          authenticatedUser={user}
          onSubmit={handleTeachingSubmit}
          onCancel={handleTeachingCancel}
        />
      )}

        {/* Impersonation Modal */}
        <ImpersonationModal
          isOpen={showImpersonationModal}
          onClose={() => setShowImpersonationModal(false)}
        />

        {/* Share Conversation Modal */}
        {shareConversationId && (
          <ShareModal
            conversationId={shareConversationId}
            onClose={() => setShareConversationId(null)}
          />
        )}
        </div>
        </main>
      </div>
    </div>
  );
};

export default ChatPage;
